/**
 * Composition root do front — o único lugar que liga core, módulos e rotas.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';
import { safeReturnTo, userManager, type SigninState } from '@/core/auth';
import { queryClient } from '@/core/query/client';
import { FullscreenSpinner } from '@/core/ui';
import { router } from './router';

/**
 * Depois que o Keycloak devolve o `code` e o AuthProvider troca por tokens,
 * limpa `?code=&state=` da URL e leva o usuário para onde ele ia.
 */
function onSigninCallback(user: User | void) {
  const state = (user?.state ?? {}) as SigninState;
  window.history.replaceState({}, document.title, safeReturnTo(state.returnTo));
}

function InnerApp() {
  const auth = useAuth();
  // O router só monta depois da primeira resolução da sessão (incluindo a
  // troca do code no callback). Sem isso, o guard da área logada rodaria
  // com "não autenticado" e mandaria para o login quem acabou de entrar.
  // É uma trava: a renovação silenciosa também liga `isLoading`, e ela não
  // pode desmontar o app no meio do uso.
  const [ready, setReady] = useState(false);
  if (!ready && !auth.isLoading) setReady(true);

  // Login, logout ou sessão expirada: reavalia os guards das rotas.
  useEffect(() => {
    if (ready) void router.invalidate();
  }, [ready, auth.isAuthenticated]);

  if (!ready) return <FullscreenSpinner />;
  return <RouterProvider router={router} context={{ auth }} />;
}

export function App() {
  return (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      <QueryClientProvider client={queryClient}>
        <InnerApp />
      </QueryClientProvider>
    </AuthProvider>
  );
}
